import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Flip2 } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { IconButton, Input, Skeleton, Stack, Textarea, Tooltip, Typography } from '@lyearn/ui';
import { Image } from '@/components/Image';
import { VideoSlideTemplateCategoryEnum } from '@/types/schema';

import { SLIDE_ITEM_CHAR_LIMIT, SLIDE_VAR_CHAR_LIMITS } from '../videoSlideConstants';
import { DeleteSlideConfirmPopover } from './DeleteSlideConfirmPopover';
import { VideoSlideTemplate, VideoTemplatePicker } from './VideoTemplatePicker';

export interface StoryboardSlide {
  slideTemplateId: string;
  heygenTemplateId: string;
  name: string;
  transcript: string;
  variables: Record<string, string>;
  previewUrl?: string;
  variableSchema?: string;
}

interface SlideEditorProps {
  slide: StoryboardSlide;
  slideIndex: number;
  avatarPreviewUrl?: string;
  isSwitchingTemplate?: boolean;
  onUpdateTranscript: (transcript: string) => void;
  onUpdateVariable: (slideIndex: number, key: string, value: string) => void;
  onDeleteSlide: (slideIndex: number) => void;
  onChangeSlideTemplate: (
    slideIndex: number,
    slideTemplateId: string,
    template: VideoSlideTemplate,
  ) => void;
}

export function SlideEditor(props: SlideEditorProps) {
  const {
    slide,
    slideIndex,
    avatarPreviewUrl,
    isSwitchingTemplate,
    onUpdateTranscript,
    onUpdateVariable,
    onDeleteSlide,
    onChangeSlideTemplate,
  } = props;
  const variables = slide.variables ?? {};
  const isDocumentImageMode = Boolean(variables.image?.trim());
  const effectivePreviewUrl = variables.image?.trim() || slide.previewUrl;
  const { t } = useTranslation('Course');
  const isTemplateSwitching = Boolean(isSwitchingTemplate);

  const getAvatarOverlayPosition = useCallback(
    (previewUrl?: string): 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' => {
      const decoded = decodeURIComponent(previewUrl || '').toLowerCase();
      if (
        decoded.includes('top_left') ||
        decoded.includes('top-left') ||
        decoded.includes('top left')
      ) {
        return 'top-left';
      }
      if (
        decoded.includes('top_right') ||
        decoded.includes('top-right') ||
        decoded.includes('top right')
      ) {
        return 'top-right';
      }
      if (
        decoded.includes('bottom_left') ||
        decoded.includes('bottom-left') ||
        decoded.includes('bottom left')
      ) {
        return 'bottom-left';
      }
      return 'bottom-right';
    },
    [],
  );

  const avatarOverlayPosition = getAvatarOverlayPosition(slide.previewUrl);
  const avatarOverlayPositionClass =
    avatarOverlayPosition === 'top-left'
      ? 'left-[15px] top-[15px'
      : avatarOverlayPosition === 'top-right'
      ? 'right-[15px] top-[15px]'
      : avatarOverlayPosition === 'bottom-left'
      ? 'left-[15px] bottom-[15px]'
      : 'right-[15px] bottom-[15px]';

  const SCALAR_KEYS = ['title', 'subtitle', 'body'] as const;
  const scalarEntries = SCALAR_KEYS.filter((key) => key in variables).map(
    (key) => [key, variables[key]] as [string, string],
  );
  const itemEntries = Object.entries(variables)
    .filter(([key]) => /^item\d+$/.test(key))
    .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));

  const CHAR_LIMITS = SLIDE_VAR_CHAR_LIMITS as Record<(typeof SCALAR_KEYS)[number], number>;

  const FIELD_LABELS: Record<(typeof SCALAR_KEYS)[number], string> = {
    title: t('Course:AiVideoGeneration.Title'),
    subtitle: t('Course:AiVideoGeneration.Subtitle'),
    body: t('Course:AiVideoGeneration.BodyText'),
  };
  const PLACEHOLDERS: Record<(typeof SCALAR_KEYS)[number], string> = {
    title: t('Course:AiVideoGeneration.EnterTitle'),
    subtitle: t('Course:AiVideoGeneration.EnterSubtitle'),
    body: t('Course:AiVideoGeneration.EnterBodyText'),
  };

  const hasVariables = scalarEntries.length > 0 || itemEntries.length > 0;
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const touchField = useCallback((fieldKey: string) => {
    setTouchedFields((prev) => (prev[fieldKey] ? prev : { ...prev, [fieldKey]: true }));
  }, []);
  const getEmptyError = useCallback((label: string) => `${label} can't be left empty`, []);
  const [deletePopoverOpen, setDeletePopoverOpen] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [thumbnailHovered, setThumbnailHovered] = useState(false);
  const [isPreviewLoaded, setIsPreviewLoaded] = useState(() => !effectivePreviewUrl);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | undefined>(undefined);
  const previewUrlRef = useRef<string | undefined>(effectivePreviewUrl);
  const slideEditorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!effectivePreviewUrl) {
      previewUrlRef.current = effectivePreviewUrl;
      setPendingPreviewUrl(undefined);
      setIsPreviewLoaded(true);
      return;
    }

    if (previewUrlRef.current !== effectivePreviewUrl) {
      previewUrlRef.current = effectivePreviewUrl;
      setIsPreviewLoaded(false);
    }
  }, [effectivePreviewUrl]);

  const blurActiveElement = useCallback(() => {
    requestAnimationFrame(() => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    });
  }, []);

  const focusSlideEditor = useCallback(() => {
    requestAnimationFrame(() => {
      slideEditorRef.current?.focus();
    });
  }, []);

  const handleChangeTemplate = useCallback(
    (slideTemplateId: string, template: VideoSlideTemplate) => {
      const nextPreviewUrl = template.preview_url ?? undefined;
      const shouldWaitForNewPreview = Boolean(
        nextPreviewUrl && nextPreviewUrl !== effectivePreviewUrl,
      );

      setTemplatePickerOpen(false);
      setDeletePopoverOpen(false);
      setThumbnailHovered(false);
      setPendingPreviewUrl(shouldWaitForNewPreview ? nextPreviewUrl : undefined);

      if (shouldWaitForNewPreview) {
        setIsPreviewLoaded(false);
      }

      blurActiveElement();
      focusSlideEditor();
      onChangeSlideTemplate(slideIndex, slideTemplateId, template);
    },
    [blurActiveElement, effectivePreviewUrl, focusSlideEditor, onChangeSlideTemplate, slideIndex],
  );

  const isWaitingForPendingPreview = Boolean(
    pendingPreviewUrl && effectivePreviewUrl !== pendingPreviewUrl,
  );
  const isSlidePreviewLoading =
    !effectivePreviewUrl || isTemplateSwitching || isWaitingForPendingPreview || !isPreviewLoaded;
  const showThumbnailControls =
    !isSlidePreviewLoading && (thumbnailHovered || deletePopoverOpen || templatePickerOpen);

  const handlePreviewLoad = useCallback(
    (_e?: unknown) => {
      setIsPreviewLoaded(true);
      setPendingPreviewUrl((currentPendingPreviewUrl) =>
        currentPendingPreviewUrl === effectivePreviewUrl ? undefined : currentPendingPreviewUrl,
      );
    },
    [effectivePreviewUrl],
  );

  useEffect(() => {
    if (!isTemplateSwitching && isWaitingForPendingPreview) {
      setPendingPreviewUrl(undefined);
      setIsPreviewLoaded(true);
    }
  }, [isTemplateSwitching, isWaitingForPendingPreview]);

  return (
    <div ref={slideEditorRef} tabIndex={-1}>
      <Stack className="mx-16 rounded-12 bg-bg-secondary p-28" direction="col">
        <Stack alignItems="start" direction="row" gap={20}>
          <Typography
            className="mt-12 font-semibold"
            color="text-text-secondary"
            variant="heading-xs">
            {String(slideIndex + 1).padStart(2, '0')}
          </Typography>
          <Stack
            className="relative w-[22rem] shrink-0 overflow-hidden rounded-8 border border-border"
            onMouseEnter={() => setThumbnailHovered(true)}
            onMouseLeave={() => setThumbnailHovered(false)}>
            <Image
              key={effectivePreviewUrl ?? `slide-preview-${slideIndex}`}
              alt={`Slide ${slideIndex + 1}`}
              className={cn(
                'w-full object-cover transition-opacity',
                isSlidePreviewLoading ? 'opacity-0' : 'opacity-100',
              )}
              height={122}
              quality={100}
              src={effectivePreviewUrl}
              width={218}
              onLoad={handlePreviewLoad}
            />
            {isDocumentImageMode && avatarPreviewUrl ? (
              <Stack
                className={cn(
                  'absolute z-15 h-[46px] w-[46px] overflow-hidden rounded-full border border-white bg-bg-primary shadow-sm',
                  avatarOverlayPositionClass,
                )}>
                <Image
                  alt="Avatar preview"
                  className="h-full w-full object-cover object-center"
                  height={46}
                  src={avatarPreviewUrl}
                  width={46}
                />
              </Stack>
            ) : null}
            {isSlidePreviewLoading ? (
              <Skeleton
                animation="wave"
                className="absolute left-0 top-0 z-10 pointer-events-none"
                height={122}
                variant="rectangular"
                width={218}
              />
            ) : null}

            {/* Hover overlay */}
            <div
              className="pointer-events-none h-full w-full absolute inset-0 z-20 transition-opacity"
              style={{
                opacity: showThumbnailControls ? 1 : 0,
                backgroundColor: showThumbnailControls ? 'rgba(0, 0, 0, 0.4)' : 'transparent',
              }}
            />

            <Stack
              alignItems="center"
              className="absolute right-8 top-8 z-20 transition-opacity"
              direction="row"
              gap={8}
              style={{
                opacity: showThumbnailControls ? 1 : 0,
              }}>
              <VideoTemplatePicker
                category={
                  isDocumentImageMode
                    ? ('IMAGE' as unknown as VideoSlideTemplateCategoryEnum)
                    : VideoSlideTemplateCategoryEnum.Default
                }
                disabled={isSlidePreviewLoading}
                selectedTemplateId={slide.slideTemplateId}
                onChangeTemplate={handleChangeTemplate}
                onOpenChange={setTemplatePickerOpen}>
                <Tooltip placement="top" title="Change Layout">
                  <IconButton
                    appearance="secondary"
                    aria-label={t('Common:Actions.Change')}
                    className={cn(
                      'bg-bg-primary hover:bg-bg-secondary',
                      templatePickerOpen ? 'border-text-primary' : '',
                    )}
                    disabled={isSlidePreviewLoading}
                    shape="square"
                    size="s"
                    variant="outlined">
                    <Flip2
                      className={cn('h-16 w-16', templatePickerOpen ? 'text-text-primary' : '')}
                    />
                  </IconButton>
                </Tooltip>
              </VideoTemplatePicker>

              <DeleteSlideConfirmPopover
                disabled={isSlidePreviewLoading}
                onConfirm={() => onDeleteSlide(slideIndex)}
                onOpenChange={setDeletePopoverOpen}
              />
            </Stack>
          </Stack>
          <Stack className="flex-1 min-w-0" direction="col" gap={20}>
            {hasVariables ? (
              isSlidePreviewLoading ? (
                <Stack direction="col" gap={8}>
                  <Typography color="text-text-primary" variant="body-short-02">
                    {t('Course:AiVideoGeneration.SlideContent')}
                  </Typography>
                  <Stack className="h-[5rem] w-full rounded-8 border border-border bg-bg-primary px-16 py-14">
                    <Typography color="text-text-placeholder" variant="body-short-01">
                      Generating slide content...
                    </Typography>
                  </Stack>
                </Stack>
              ) : (
                <Stack direction="col" gap={8}>
                  {scalarEntries.map(([key, value]) => {
                    const limit = CHAR_LIMITS[key as keyof typeof CHAR_LIMITS];
                    const label = FIELD_LABELS[key as keyof typeof FIELD_LABELS];
                    const isTouched = Boolean(touchedFields[key]);
                    const isEmpty = !value?.trim();
                    const errorText = isTouched && isEmpty ? getEmptyError(label) : null;
                    return (
                      <Stack key={key} direction="col" gap={4}>
                        <Stack direction="row" justifyContent="between">
                          <Typography color="text-text-primary" variant="body-short-02">
                            {label}
                          </Typography>
                        </Stack>
                        {key === 'body' ? (
                          <>
                            <Textarea
                              className="w-full"
                              maxRows={10}
                              minRows={6}
                              placeholder={PLACEHOLDERS[key as keyof typeof PLACEHOLDERS]}
                              value={value}
                              onBlur={() => touchField(key)}
                              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                                onUpdateVariable(slideIndex, key, e.target.value.slice(0, limit))
                              }
                            />
                            {errorText ? (
                              <Typography
                                className="mt-4"
                                color="text-text-error"
                                variant="body-short-03">
                                {errorText}
                              </Typography>
                            ) : null}
                          </>
                        ) : (
                          <>
                            <Input
                              characterLimit={limit}
                              className="w-full"
                              placeholder={PLACEHOLDERS[key as keyof typeof PLACEHOLDERS]}
                              value={value}
                              onBlur={() => touchField(key)}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                onUpdateVariable(slideIndex, key, e.target.value)
                              }
                            />
                            {errorText ? (
                              <Typography
                                className="mt-4"
                                color="text-text-error"
                                variant="body-short-03">
                                {errorText}
                              </Typography>
                            ) : null}
                          </>
                        )}
                      </Stack>
                    );
                  })}

                  {itemEntries.map(([key, value], idx) => {
                    const label = `Bullet Item ${idx + 1}`;
                    const isTouched = Boolean(touchedFields[key]);
                    const isEmpty = !value?.trim();
                    const errorText = isTouched && isEmpty ? getEmptyError(label) : null;
                    return (
                      <Stack key={key} direction="col" gap={4}>
                        <Typography color="text-text-primary" variant="body-short-02">
                          {label}
                        </Typography>
                        <>
                          <Input
                            characterLimit={SLIDE_ITEM_CHAR_LIMIT}
                            className="w-full"
                            placeholder="Type list item here"
                            startAdornment={
                              <Typography
                                className="shrink-0 pr-8"
                                color="text-text-primary"
                                variant="body-short-01">
                                •
                              </Typography>
                            }
                            value={value}
                            onBlur={() => touchField(key)}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              onUpdateVariable(slideIndex, key, e.target.value)
                            }
                          />
                          {errorText ? (
                            <Typography
                              className="mt-4"
                              color="text-text-error"
                              variant="body-short-03">
                              {errorText}
                            </Typography>
                          ) : null}
                        </>
                      </Stack>
                    );
                  })}
                </Stack>
              )
            ) : null}

            <Stack direction="col" gap={8}>
              <Typography color="text-text-primary" variant="body-short-02">
                {t('Course:AiVideoGeneration.SpeechText')}
              </Typography>
              {isSlidePreviewLoading ? (
                <Stack className="h-[12rem] w-full rounded-8 border border-border bg-bg-primary px-16 py-14">
                  <Typography color="text-text-placeholder" variant="body-short-01">
                    Generating speech text...
                  </Typography>
                </Stack>
              ) : (
                <>
                  <Textarea
                    className="w-full"
                    maxRows={6}
                    minRows={3}
                    placeholder={t('Course:AiVideoGeneration.EnterSpeechText')}
                    value={slide.transcript}
                    onBlur={() => touchField('transcript')}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                      onUpdateTranscript(e.target.value)
                    }
                  />
                  {touchedFields.transcript && !slide.transcript.trim() ? (
                    <Typography className="mt-4" color="text-text-error" variant="body-short-03">
                      {getEmptyError(t('Course:AiVideoGeneration.SpeechText'))}
                    </Typography>
                  ) : null}
                </>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </div>
  );
}
